import { useMutation, useQuery } from '@tanstack/react-query'
import { type RefObject, useEffect, useState } from 'react'
import { useOnScreen } from '@/scopes/common/components/hooks/useOnScreen'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createProductAdsListViewedEvent } from '@/scopes/product-ads/domain/analytics'
import { ProductAd, ProductAdComponentType } from '@/scopes/product-ads/domain/types'
import { productAdsAPIClient } from '../infrastructure/api'

interface UseProductAdsProps {
  contextItemId: string
  componentType: ProductAdComponentType
}

interface UseDestinationFilteredProductAdsProps extends UseProductAdsProps {
  destinations: Partial<Record<'Country' | 'Region', string[]>>
}

export function useProductAds({ contextItemId, componentType }: UseProductAdsProps) {
  const componentFilter = componentType ? { component: componentType } : {}

  const { isLoading, data, isFetched, error } = useQuery({
    queryKey: [`product-ads-${componentType}`, contextItemId, JSON.stringify(componentFilter)],
    queryFn: () => productAdsAPIClient.getFilteredProductAds(contextItemId, componentFilter),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  })

  return {
    isLoading,
    data,
    isFetched,
    error,
  }
}

export function useDestinationFilteredProductAds({ contextItemId, destinations, componentType }: UseDestinationFilteredProductAdsProps) {
  const filters = {
    ...destinations,
    component: componentType,
  }
  const { isLoading, data, isFetched } = useQuery({
    queryKey: [`product-ads-${componentType}`, contextItemId, JSON.stringify(filters)],
    queryFn: () => productAdsAPIClient.getFilteredProductAds(contextItemId, filters),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  })

  return {
    isLoading,
    data,
    isFetched,
  }
}

export function useProductAdsRendered(endpoint: string) {
  const { data, isFetched } = useQuery({
    queryKey: ['product-ads-rendered', endpoint],
    queryFn: () => productAdsAPIClient.notifyHomepageAds(endpoint),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  })

  return {
    data,
    isFetched,
  }
}

export function useUpdateProductAdViewed(endpoint: string) {
  const { mutateAsync: update } = useMutation({
    mutationFn: () => productAdsAPIClient.notifyHomepageAds(endpoint),
    retry: false,
  })

  return {
    update,
  }
}

export function useProductAdsListViewed(data: ProductAd[] | undefined, type: string, ref: RefObject<HTMLElement | null>) {
  const isOnScreen = useOnScreen(ref)
  const { sendEvent } = useAnalytics()
  const [alreadySent, setAlreadySent] = useState(false)

  useEffect(() => {
    if (isOnScreen && data && !alreadySent) {
      sendEvent(createProductAdsListViewedEvent({ ads: data, type }))
      setAlreadySent(true)
    }
  }, [isOnScreen])
}
